import 'intersection-observer';
import throttle from 'lodash/throttle';

class EnterClass {
  constructor(node, options = {}) {
    this.props = {
      rootMargin: '0px 0px 0px 0px',
      threshold: [0],
      props: [],
      ...options,
    };
    this.node = node;
    this.node.decorator = this;

    this.addStaggerDelays();

    // IntersectionObserver is the nice-path trigger for the fade-in animation.
    // On some devices / scroll setups it only ever delivers its initial
    // observe-time callback and never re-fires as the element scrolls into
    // view, which would leave the content stuck at opacity: 0 and unreadable.
    // The scroll/resize fallback below guarantees the content is revealed
    // regardless of whether the observer keeps firing.
    if (typeof IntersectionObserver !== 'undefined') {
      this.registerObserver();
    }

    window.addEventListener('scroll', this.checkInView, { passive: true });
    window.addEventListener('resize', this.checkInView);
    this.checkInView();
  }

  addStaggerDelays() {
    const staggerChildren = this.node.querySelectorAll('.animate-stagger');
    if (staggerChildren && staggerChildren.length) {
      for (let i = 0; i < staggerChildren.length; i++) {
        const child = staggerChildren[i];
        child.style.animationDelay = `${Math.min(8, i) * 0.1}s`;
      }
    }
  }

  registerObserver = () => {
    const { rootMargin, threshold } = this.props;
    this.observer = new IntersectionObserver(this.onIntersection, {
      rootMargin,
      threshold,
    });
    this.observer.observe(this.node);
  };

  reveal = () => {
    this.node.classList.add('animate-visible');
    if (this.observer) {
      this.observer.unobserve(this.node);
    }
    window.removeEventListener('scroll', this.checkInView);
    window.removeEventListener('resize', this.checkInView);
  };

  // Throttled manual viewport check used as a fallback for the observer.
  checkInView = throttle(() => {
    const rect = this.node.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < viewportHeight && rect.bottom > 0) {
      this.reveal();
    }
  }, 200);

  onIntersection = entries => {
    entries.forEach(({ intersectionRatio }) => {
      if (intersectionRatio > 0) {
        this.reveal();
      }
    });
  };
}

export default (node, props) => new EnterClass(node, props);
